import { IFormInput } from "./UploadDocumentsDialog";
import { UseFormRegister } from "react-hook-form";
import { UserDocument } from "@/shared/types/documentTypes";
import { DocumentType } from "@/shared/types/documentTypes";
import { StagedDocument } from "@/shared/hooks/useDocumentUpload";
import { IconButton } from "@/shared/components/ui/icon-button";
import { cn } from "@/shared/utils/cn";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { X, FileText } from "lucide-react";

interface UploadedDocumentProps<T extends DocumentType> {
  documentTypes: { id: T; label: string }[];
  document: UserDocument | StagedDocument | { name?: string; fileName?: string };
  /**
   * Optional react-hook-form register. When provided, the dropdown's
   * onChange forwards to the registered field; when absent, only the
   * local `onDocumentTypeChange` callback fires.
   */
  register?: UseFormRegister<IFormInput>;
  ix: number;
  onDocumentTypeChange: (v: T) => void;
  onDocumentRemove: () => void;
  documentType: T;
  titleClassName?: string;
  isErrored: boolean;
}

export function UploadedDocument<T extends DocumentType>({
  documentTypes,
  document,
  register,
  ix,
  onDocumentTypeChange,
  onDocumentRemove,
  documentType,
  titleClassName,
  isErrored,
}: UploadedDocumentProps<T>) {
  const registeredField = register ? register(`documentTypes.${ix}.documentType`) : null;
  // StagedDocument has 'name' from File, UserDocument has both 'name' and 'fileName'
  const displayName = document.name || ("fileName" in document ? document.fileName : "") || "";
  const name = displayName.replace(".pdf", "");

  // Get the current document type label
  const currentDocumentTypeLabel =
    documentTypes.find((type) => type.id === documentType)?.label || documentType;
  return (
    <div className="flex flex-col md:flex-row items-start md:items-center rounded-md border border-stone-100 gap-4 p-4 max-w-full w-full bg-stone-50">
      <div className="flex-grow flex flex-row gap-2 items-center w-full md:w-auto min-w-0">
        <div className={cn(["flex-grow min-w-0 justify-center", titleClassName])}>
          <div className="ml-3 flex flex-row items-center gap-3 min-w-0 max-w-full">
            <FileText className="w-6 h-6 text-stone-400 flex-shrink-0" />
            <p className="truncate min-w-0 flex-1">{name}</p>
            <div className="px-2 h-[22px] bg-stone-200 rounded-sm text-sm font-medium text-stone-600 text-center flex items-center justify-center flex-shrink-0">
              {displayName.split(".").pop()?.toUpperCase()}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-auto p-2 md:p-0 flex flex-row gap-2 items-center">
        {documentTypes.length > 1 ? (
          // Multiple document types available - show dropdown
          <Select
            onValueChange={(v: T) => {
              registeredField?.onChange({ target: { value: v } });
              onDocumentTypeChange(v);
            }}
            defaultValue={documentType}
          >
            <SelectTrigger
              className={cn(
                "overflow-hidden col-span-3 flex-1 md:w-[200px]",
                isErrored && "ring-destructive ring-2 border-none",
              )}
            >
              <SelectValue
                className="w-full overflow-ellipsis text-stone-600"
                placeholder="Select type"
              >
                {currentDocumentTypeLabel}
              </SelectValue>
            </SelectTrigger>
            <SelectContent onChange={registeredField?.onChange}>
              <SelectGroup>
                {documentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        ) : documentTypes.length === 1 ? (
          // Single document type - show as static label (no dropdown needed)
          <div className="flex items-center px-3 py-2 bg-subtle rounded-md text-sm text-stone-600">
            {documentTypes[0].label}
          </div>
        ) : null}
        <IconButton onClick={onDocumentRemove} label="Remove document" tooltip={false}>
          <X />
        </IconButton>
      </div>
    </div>
  );
}
